import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Paper,
  Snackbar,
  Alert,
} from "@mui/material";
import apiService from "../components/ReasuableFuncations/api";

const RiskManagementModule = () => {
  const [riskData, setRiskData] = useState({
    description: "",
    type: "",
    probability: "",
    impact: "",
    mitigationStrategy: "",
    owner: "",
    status: "",
    reviewDate: "",
    projectName: "",
    comments: "",
    riskSource: "",
  });

  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [owners, setOwners] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false); // New loading state

  const fetchProjects = async () => {
    const userData = JSON.parse(localStorage.getItem("user"));
    if (!userData) {
      console.error("User data not found. Redirecting to login.");
      return;
    }
    const {
      selectedOption,
      regionalNetworkOption,
      memberStateOption,
      countryOption,
    } = userData;
    let url = `/api/projects-stake`;

    try {
      let response;

      if (selectedOption === "Comesa") {
        response = await apiService.get(`/api/project-data`);
      } else if (
        selectedOption === "Regional Network" &&
        regionalNetworkOption
      ) {
        response = await apiService.get(
          `${url}?regionalNetwork=${regionalNetworkOption}`
        );
      } else if (selectedOption === "Member State" && memberStateOption) {
        response = await apiService.get(`${url}?country=${countryOption}`);
      }

      if (response && response.statusText !== "OK") {
        throw new Error("Failed to fetch projects");
      }

      const data = response.data;
      setProjects(data);
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  const fetchOwners = async () => {
    const userData = JSON.parse(localStorage.getItem("user"));
    if (!userData) {
      console.error("User data not found. Redirecting to login.");
      return;
    }
    const { regionalNetworkOption, countryOption } = userData;
    let url = `/api/projects-risk`;
    try {
      let response;
      response = await apiService.get(
        `${url}?regionalNetwork=${regionalNetworkOption}&country=${countryOption}`
      );

      if (response && response.statusText !== "OK") {
        throw new Error("Failed to fetch stakeholders");
      }

      const data = response.data;
      setOwners(data);
    } catch (error) {
      console.error("Error fetching stakeholders:", error);
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchOwners();
  }, []);

  useEffect(() => {
    // Scroll to the top of the page when the component mounts
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setRiskData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "",
      }));
    }
  };

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const validateForm = () => {
    const newErrors = {};
    if (!riskData.description.trim())
      newErrors.description = "Description is required";
    if (!riskData.type) newErrors.type = "Type is required";
    if (!riskData.probability)
      newErrors.probability = "Probability is required";
    if (!riskData.impact) newErrors.impact = "Impact is required";
    if (!riskData.mitigationStrategy.trim())
      newErrors.mitigationStrategy = "Mitigation strategy is required";
    if (!riskData.owner) newErrors.owner = "Risk owner is required";
    if (!riskData.status) newErrors.status = "Status is required";
    if (!riskData.reviewDate) {
      newErrors.reviewDate = "Review date is required";
    } else if (riskData.reviewDate <= getTodayDate()) {
      newErrors.reviewDate = "Review date must be in the future";
    }
    if (!riskData.riskSource) newErrors.riskSource = "Risk source is required";
    if (!riskData.projectName)
      newErrors.projectName = "Project Name is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (validateForm()) {
      setLoading(true); // Start loading

      const userData = JSON.parse(localStorage.getItem("user"));
      if (!userData) {
        console.error("User data not found. Redirecting to login.");
        setLoading(false); // Stop loading
        return;
      }
      const {
        selectedOption,
        regionalNetworkOption,
        memberStateOption,
        countryOption,
      } = userData;

      const requestData = {
        ...riskData,
        selectedOption,
        regionalNetworkOption,
        memberStateOption,
        countryOption,
      };

      try {
        const response = await apiService.post(`/api/risks`, requestData);

        if (response.statusText === "Created") {
          setSnackbar({
            open: true,
            message: "Risk data submitted successfully!",
            severity: "success",
          });

          setRiskData({
            description: "",
            type: "",
            probability: "",
            impact: "",
            mitigationStrategy: "",
            owner: "",
            status: "",
            reviewDate: "",
            projectName: "",
            comments: "",
            riskSource: "",
          });
        } else {
          const errorData = response.data;
          setSnackbar({
            open: true,
            message: errorData.message || "Failed to submit risk data",
            severity: "error",
          });
        }
      } catch (error) {
        console.error("Error submitting risk data:", error);
        setSnackbar({
          open: true,
          message: "Failed to submit risk data",
          severity: "error",
        });
      } finally {
        setLoading(false); // Stop loading after completion
      }
    } else {
      setSnackbar({
        open: true,
        message: "Please fill in all required fields correctly.",
        severity: "error",
      });
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Container maxWidth="md" sx={{ paddingRight: "32px" }}>
      <Paper elevation={20} sx={{ p: 4, mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          Risk Entry Form
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Risk Description"
                name="description"
                value={riskData.description}
                onChange={handleInputChange}
                error={!!errors.description}
                helperText={errors.description}
                required
                multiline
                rows={2}
                maxRows={10}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& textarea": { resize: "vertical" },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.projectName}>
                <InputLabel>Project Name*</InputLabel>
                <Select
                  name="projectName"
                  value={riskData.projectName}
                  onChange={handleInputChange}
                  label="Project Name"
                  required
                >
                  {projects.map((project) => (
                    <MenuItem key={project._id} value={project._id}>
                      {project.projectName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.projectName && (
                  <Typography color="error">{errors.projectName}</Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.type}>
                <InputLabel>Risk Type*</InputLabel>
                <Select
                  name="type"
                  value={riskData.type}
                  onChange={handleInputChange}
                  label="Risk Type"
                  required
                >
                  <MenuItem value="Financial">Financial</MenuItem>
                  <MenuItem value="Operational">Operational</MenuItem>
                  <MenuItem value="Strategic">Strategic</MenuItem>
                  <MenuItem value="External">External</MenuItem>
                </Select>
                {errors.type && (
                  <Typography color="error">{errors.type}</Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Risk Source"
                name="riskSource"
                value={riskData.riskSource}
                onChange={handleInputChange}
                error={!!errors.riskSource}
                helperText={errors.riskSource}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.probability}>
                <InputLabel>Probability*</InputLabel>
                <Select
                  name="probability"
                  value={riskData.probability}
                  onChange={handleInputChange}
                  label="Probability"
                  required
                >
                  <MenuItem value="Low">Low</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                  <MenuItem value="High">High</MenuItem>
                </Select>
                {errors.probability && (
                  <Typography color="error">{errors.probability}</Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.impact}>
                <InputLabel>Impact*</InputLabel>
                <Select
                  name="impact"
                  value={riskData.impact}
                  onChange={handleInputChange}
                  label="Impact"
                  required
                >
                  <MenuItem value="Low">Low</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                  <MenuItem value="High">High</MenuItem>
                </Select>
                {errors.impact && (
                  <Typography color="error">{errors.impact}</Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.owner}>
                <InputLabel>Risk Owner*</InputLabel>
                <Select
                  name="owner"
                  value={riskData.owner}
                  onChange={handleInputChange}
                  label="Risk Owner"
                  required
                >
                  {owners.map((owner) => (
                    <MenuItem key={owner._id} value={owner._id}>
                      {owner.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.owner && (
                  <Typography color="error">{errors.owner}</Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.status}>
                <InputLabel>Status*</InputLabel>
                <Select
                  name="status"
                  value={riskData.status}
                  onChange={handleInputChange}
                  label="Status"
                  required
                >
                  <MenuItem value="Open">Open</MenuItem>
                  <MenuItem value="Closed">Closed</MenuItem>
                  <MenuItem value="Mitigated">Mitigated</MenuItem>
                </Select>
                {errors.status && (
                  <Typography color="error">{errors.status}</Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Mitigation Strategy"
                name="mitigationStrategy"
                value={riskData.mitigationStrategy}
                onChange={handleInputChange}
                error={!!errors.mitigationStrategy}
                helperText={errors.mitigationStrategy}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Review Date"
                name="reviewDate"
                type="date"
                value={riskData.reviewDate}
                onChange={handleInputChange}
                error={!!errors.reviewDate}
                helperText={errors.reviewDate}
                required
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: getTodayDate()
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Comments"
                name="comments"
                value={riskData.comments}
                onChange={handleInputChange}
                multiline
                // rows={2}
                maxRows={4}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading} // Disable button if loading
                fullWidth
              >
                {loading ? "Submitting..." : "Submit"}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default RiskManagementModule;
